import { useEffect, useRef } from 'react';
import { PRE_REG_GEMS, fmtMonDayOrdinal } from '../campaign';
import { Crop, BgRot, Lantern, StatBox } from './Shared';
import { BackIcon, ShareIcon, ClockIcon } from './Icons';
import { useLottie, loadHubPrizes } from './Lottie';

/* Status strips under the Gems / Chests counts (prototype render() and
   renderRewards()). `hub` picks the hub wording; the rewards page is quieter
   (no claim call, no "complete a streak" nudge). Dates come from the campaign
   (launch / grand draw) formatted "Oct 18th". Returns
   { text, muted, claimable } or null. */
export function gemsChipFor(phase, gems, unopened, { hub = false, registered = true, preRegGems = PRE_REG_GEMS, launchLabel = 'Oct 18th' } = {}) {
  if (phase === 'pre') {
    if (hub && !registered) return { text: `Claim ${preRegGems} Gems`, muted: false, claimable: true };
    if (hub || unopened > 0) return { text: `Unlocks ${launchLabel}`, muted: true };
    return null;
  }
  if (unopened > 0) return { text: `${unopened} ready to open`, muted: false };
  if (phase === 'live' || gems > 0) return { text: 'All opened', muted: true };
  return null;
}
/* `drawn`: the reveal day (grandPrizes.drawOn) has come — until then the
   chests wait for the draw even after the campaign has ended */
export function chestsChipFor(phase, chests, { hub = false, drawLabel = 'Nov 13th', drawn = phase === 'ended' } = {}) {
  if (phase === 'pre') return hub ? { text: `Unlocks ${drawLabel}`, muted: true } : null;
  if (drawn && chests > 0) return { text: `Drawn on ${drawLabel}`, muted: false };
  if (chests > 0) return { text: hub ? `${chests} in draw · opens ${drawLabel}` : `${chests} in the grand draw`, muted: true };
  return hub ? { text: 'Complete a streak', muted: true } : null;
}

/* Hub hero: turning ray backdrop, lanterns, top bar, launch countdown, logo
   lockup, summary tiles and the looping prize reveal (a Lottie). `heroRef`
   is used by the scroll effect in index.js; `covered` pauses the reveal
   while a full-screen page sits over the hub; `flash` lights the Gems tile
   up after the free Gems were reserved, `chestsFlash` the Chests tile
   after the survey's Golden Chest landed. */
export default function Hero({
  phase, registered, preRegGems = PRE_REG_GEMS, gems, unopened = 0, chests, countdown, launch, drawOn, drawn = phase === 'ended',
  heroRef, covered = false, showHowTo = true, flash = false, chestsFlash = false,
  onBack, onHowTo, onShare, onGems, onChests, onViewPrizes,
}) {
  const pre = phase === 'pre';
  const launchLabel = launch ? fmtMonDayOrdinal(launch) : 'Oct 18th';
  const drawLabel = drawOn ? fmtMonDayOrdinal(drawOn) : 'Nov 13th';
  const gemsChip = gemsChipFor(phase, gems, unopened, { hub: true, registered, preRegGems, launchLabel });
  const chestsChip = chestsChipFor(phase, chests, { hub: true, drawLabel, drawn });

  /* the reveal runs whenever the hub is on screen: from the top the first
     time, picking up where it left off after a page hands back to the hub
     (prototype playReveal / instantReveal / stopReveal) */
  const lotRef = useRef(null);
  const reveal = useLottie(lotRef, { load: loadHubPrizes, loop: true });
  useEffect(() => {
    if (covered) reveal.pause(); else reveal.play();
  }, [covered, reveal]);

  return (
    <div className="gq-hero" ref={heroRef}>
      <BgRot />
      <Lantern pos="l1" />
      <Lantern pos="l2" mirror />
      <Lantern pos="l3" chain="chain" />
      <Lantern pos="l4" chain="chain" mirror />
      <div className="gq-topscrim" />

      <button type="button" className="gq-back" onClick={onBack} aria-label="Back"><BackIcon /></button>
      {showHowTo && (
        <button type="button" className="gq-howto" onClick={onHowTo}><span>How to play</span></button>
      )}
      <button type="button" className="gq-sharebtn" onClick={onShare} aria-label="Share"><ShareIcon /></button>

      {pre && (
        <div className="gq-countdown">
          <div className="gq-ico"><ClockIcon /></div>
          <span>{countdown}</span>
        </div>
      )}

      <Crop kind="logo" className="gq-logo" alt="The Golden Quest" />
      <Crop kind="watch" className="gq-hero-watch gq-rot" />
      <Crop kind="iphone" className="gq-hero-iphone gq-rot" />
      <div className="gq-t gq-subtitle">
        {phase === 'ended'
          ? 'Thanks for playing. The winners are in.'
          : 'Win BTC & Gold prizes with Gems,  win mega prizes with Golden Chests'}
      </div>

      <StatBox kind="gems" num={gems} label="Gems" chip={gemsChip} flash={flash} onClick={onGems} />
      <StatBox kind="chests" num={chests} label="Chests" chip={chestsChip} flash={chestsFlash} onClick={onChests} />

      <div className="gq-reveal">
        <div className="gq-prizelot" ref={lotRef} aria-hidden="true" />
        <button type="button" className="gq-viewall" onClick={onViewPrizes}><span>View all prizes</span></button>
      </div>
    </div>
  );
}
